"use client";

import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import Link from "next/link";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";

type Integration = {
  name: string;
  img: string;
  round?: boolean;
};

const ALL_INTEGRATIONS: Integration[] = [
  { name: "DUNE SIM", img: "/images/homepage/integrations/dune-sim.png" },
  { name: "GOOGLE SHEETS", img: "/images/homepage/integrations/google-sheets.png" },
  { name: "TELEGRAM", img: "/images/homepage/integrations/telegram.svg" },
  { name: "QUICKBOOKS", img: "/images/homepage/quickbook-icon.svg" },
  { name: "GMAIL", img: "/images/homepage/integrations/gmail.svg" },
  { name: "NOTION", img: "/images/homepage/integrations/notion.png" },
  { name: "COINGECKO", img: "/images/homepage/integrations/coingecko.svg" },
  { name: "COIN GLASS", img: "/images/homepage/integrations/coinglass.png", round: true },
  { name: "OPENROUTER", img: "/images/homepage/integrations/openrouter.png" },
  { name: "DEBANK", img: "/images/homepage/integrations/debank.png", round: true },
  { name: "COINBASE", img: "/images/homepage/integrations/coinbase.png", round: true },
  { name: "TURNKEY", img: "/images/homepage/integrations/turnkey.png" },
  { name: "UNISWAP", img: "/images/homepage/integrations/uniswap.png" },
  { name: "SHOPIFY", img: "/images/homepage/integrations/shopify.svg" },
  { name: "SLACK", img: "/images/homepage/integrations/slack.svg" },
  { name: "MAGNA", img: "/images/homepage/integrations/magna.png" },
  { name: "STRIPE", img: "/images/homepage/integrations/stripe.png", round: true },
  { name: "RAILGUN", img: "/images/homepage/integrations/railgun.png", round: true },
  { name: "DISCORD", img: "/images/homepage/integrations/discord.svg" },
  { name: "ZERION", img: "/images/homepage/integrations/zerion.png", round: true },
];

const COLUMNS = 6;
const TOTAL_CELLS = COLUMNS * 2;
const SWAP_INTERVAL_MS = 4000;

function IntegrationGridDivider({ corners }: { corners?: boolean }) {
  return (
    <div className="relative z-[3] mx-0 flex h-[19px] items-center justify-center md:-mx-4 2xl:mx-0 2xl:gap-3">
      <div className="bg-ds-neutral-200 h-px min-w-0 flex-1 md:min-w-4" />
      <div className="flex w-full max-w-[1440px] items-center gap-2">
        {Array.from({ length: COLUMNS + 1 }).map((_, i) => {
          const isBigCircle = corners && (i === 0 || i === COLUMNS);
          return (
            <Fragment key={i}>
              {i > 0 && <div className="bg-ds-neutral-200 h-px min-w-0 flex-1" />}
              {isBigCircle ? (
                <div className="bg-ds-surface-primary shadow-ds-white-pressed size-[19px] shrink-0 rounded-full" />
              ) : (
                <div className="flex size-[19px] shrink-0 items-center justify-center">
                  <div className="bg-ds-neutral-300 size-[6px] rounded-full" />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="bg-ds-neutral-200 h-px min-w-0 flex-1 md:min-w-4" />
    </div>
  );
}

function IntegrationCard({ integration, compact }: { integration: Integration; compact?: boolean }) {
  if (compact) {
    return (
      <div className="rounded-ds-lg border-ds-neutral-200 bg-ds-surface-primary flex aspect-square items-center justify-center border p-2 backdrop-blur-[8px]">
        <div className="border-ds-neutral-200 shadow-ds-b3-card-subtle relative flex size-full flex-col items-center justify-center gap-2 overflow-clip rounded-lg border bg-white">
          <img
            alt={integration.name}
            src={integration.img}
            className={`size-[48px] object-contain ${integration.round ? "rounded-ds-md" : ""}`}
          />
          <p className="text-ds-text-subtle text-2xs text-center font-mono leading-tight">{integration.name}</p>
          <div className="bg-ds-neutral-300 absolute left-[6px] top-[6px] size-[4px] rounded-full" />
          <div className="bg-ds-neutral-300 absolute right-[6px] top-[6px] size-[4px] rounded-full" />
          <div className="bg-ds-neutral-300 absolute bottom-[6px] right-[6px] size-[4px] rounded-full" />
          <div className="bg-ds-neutral-300 absolute bottom-[6px] left-[6px] size-[4px] rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-ds-2xl border-ds-neutral-200 bg-ds-surface-primary flex h-[200px] w-[200px] shrink-0 items-center justify-center border p-3 backdrop-blur-[8px]">
      <div className="rounded-ds-md border-ds-neutral-200 shadow-ds-b3-card-subtle relative flex size-full flex-col items-center justify-center gap-5 overflow-clip border bg-white">
        <img
          alt={integration.name}
          src={integration.img}
          className={`size-[90px] object-contain ${integration.round ? "rounded-ds-2xl" : ""}`}
        />
        <p className="text-ds-text-subtle whitespace-nowrap font-mono text-xl leading-none">{integration.name}</p>
        <div className="bg-ds-neutral-300 absolute left-[10px] top-[10px] size-[6px] rounded-full" />
        <div className="bg-ds-neutral-300 absolute right-[10px] top-[10px] size-[6px] rounded-full" />
        <div className="bg-ds-neutral-300 absolute bottom-[10px] right-[10px] size-[6px] rounded-full" />
        <div className="bg-ds-neutral-300 absolute bottom-[10px] left-[10px] size-[6px] rounded-full" />
      </div>
    </div>
  );
}

/**
 * Manages which integrations are visible in the 12 cells.
 * Every interval, picks a random visible cell and swaps it
 * with a random integration from the hidden pool.
 */
const STAGGER_MS = 800;

function useIntegrationSwap() {
  const [visible, setVisible] = useState<number[]>(() => Array.from({ length: TOTAL_CELLS }, (_, i) => i));
  const visibleRef = useRef(visible);
  const pendingTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const swapOne = useCallback((cellIndex: number, newIntegration: number) => {
    setVisible(prev => {
      const next = [...prev];
      next[cellIndex] = newIntegration;
      return next;
    });
  }, []);

  const scheduleSwaps = useCallback(() => {
    const current = visibleRef.current;
    const hiddenIndices = ALL_INTEGRATIONS.map((_, i) => i).filter(i => !current.includes(i));
    if (hiddenIndices.length === 0) return;

    const swapCount = Math.min(4, hiddenIndices.length);
    const cellIndices = Array.from({ length: TOTAL_CELLS }, (_, i) => i);
    const shuffledCells = cellIndices.sort(() => Math.random() - 0.5).slice(0, swapCount);
    const shuffledHidden = [...hiddenIndices].sort(() => Math.random() - 0.5).slice(0, swapCount);

    // Apply the first swap immediately
    swapOne(shuffledCells[0], shuffledHidden[0]);

    // Schedule subsequent swaps with a stagger
    for (let i = 1; i < swapCount; i++) {
      const tid = setTimeout(() => swapOne(shuffledCells[i], shuffledHidden[i]), i * STAGGER_MS);
      pendingTimeouts.current.push(tid);
    }
  }, [swapOne]);

  useEffect(() => {
    const intervalId = setInterval(scheduleSwaps, SWAP_INTERVAL_MS);
    const timeouts = pendingTimeouts.current;

    return () => {
      clearInterval(intervalId);
      timeouts.forEach(clearTimeout);
    };
  }, [scheduleSwaps]);

  return visible;
}

function IntegrationGridRow({ cells }: { cells: number[] }) {
  return (
    <div className="mx-auto flex w-full max-w-[1440px] px-[9px]">
      {cells.map((integrationIndex, i) => (
        <div
          key={i}
          className={`border-ds-neutral-200 flex h-[200px] flex-1 items-center justify-center border-l ${i === COLUMNS - 1 ? "border-r" : ""}`}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={integrationIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              <IntegrationCard integration={ALL_INTEGRATIONS[integrationIndex]} />
            </motion.div>
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

export function IntegrationsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const headingOpacity = useTransform(scrollYProgress, [0.02, 0.15, 0.82, 0.95], [0, 1, 1, 0]);
  const headingY = useTransform(scrollYProgress, [0.02, 0.15, 0.82, 0.95], [40, 0, 0, -40]);

  const visible = useIntegrationSwap();
  const row1 = visible.slice(0, COLUMNS);
  const row2 = visible.slice(COLUMNS);

  return (
    <section
      ref={sectionRef}
      id="integrations"
      className="flex flex-col items-center justify-center py-12 lg:py-[100px]"
    >
      {/* Heading */}
      <motion.div
        className="mx-auto flex max-w-[1280px] flex-col items-center gap-6 px-8"
        style={{ opacity: headingOpacity, y: headingY }}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-ds-alpha-shadow-30 font-mono text-base font-medium">
            <span>[</span>
            <span className="text-ds-brand-600">03</span>
            <span>/05] INTEGRATIONS</span>
          </p>
          <h2 className="text-ds-text-subtle leading-label max-w-[900px] text-4xl font-medium tracking-[-1.44px] md:text-7xl">
            Thousands of Integrations. Deep Context.
          </h2>
        </div>
        <p className="text-ds-text-subtle leading-30 max-w-[768px] text-center text-xl">
          We codify operational context across blockchains, apps, and SaaS platforms, enabling deterministic,
          precision-based execution.
        </p>
        <Link
          href="/integrations"
          className="rounded-ds-md shadow-ds-brand relative flex h-[48px] items-center gap-2 overflow-clip px-6 py-[14px]"
        >
          <div className="rounded-ds-md from-ds-brand-500 to-ds-brand-600 pointer-events-none absolute inset-0 bg-gradient-to-b" />
          <img
            src="/images/homepage/integrations/magnifier.svg"
            alt="Search integrations"
            className="relative size-[30px]"
          />
          <span className="leading-label relative text-base font-medium text-white">See More Integrations</span>
          <span className="rounded-inherit shadow-ds-inner-light pointer-events-none absolute inset-0" />
        </Link>
      </motion.div>

      {/* Mobile compact grid */}
      <div className="mt-6 grid w-full grid-cols-2 gap-4 px-4 lg:hidden">
        {visible.slice(0, 8).map((integrationIndex, i) => (
          <AnimatePresence key={i} mode="wait">
            <motion.div
              key={integrationIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              <IntegrationCard integration={ALL_INTEGRATIONS[integrationIndex]} compact />
            </motion.div>
          </AnimatePresence>
        ))}
      </div>

      {/* Desktop integration grid: 2 rows x 6 columns */}
      <div className="mt-8 hidden w-full lg:block">
        <IntegrationGridDivider corners />
        <IntegrationGridRow cells={row1} />
        <IntegrationGridDivider />
        <IntegrationGridRow cells={row2} />
        <IntegrationGridDivider corners />
      </div>
    </section>
  );
}
